/**
 * intc-pack-ladder.js
 *
 * Adds a per-unit price + savings-badge + total-price "meta" block onto each
 * existing Pack Size option row (rendered generically by the shared
 * snippets/product-variant-picker.liquid + snippets/product-variant-options.liquid,
 * which we do not touch). This is a display layer only — it never creates or
 * changes any <input>, so native Dawn variant selection keeps working as-is.
 *
 * Data source: snippets/intc-pack-ladder-data.liquid emits
 *   <script type="application/json" id="intc-pack-prices-{{ section.id }}"
 *           data-pack-option-position="1|2|3">[...variants...]</script>
 * and a second payload of merchant-configured badge/note copy:
 *   <script type="application/json" id="intc-pack-tags-{{ section.id }}">
 *           [{ value, badge, note }, ...]</script>
 *
 * Loaded via a plain <script src> tag — no ES module imports, no globals
 * beyond the two event listeners registered below.
 */
(function () {
  'use strict';

  // Same client-confirmed free-shipping threshold as assets/intc-shipping-bar.js.
  var INTC_FREE_SHIPPING_CENTS = 6000;
  var INTC_FREE_SHIPPING_SUFFIX = /\s*·?\s*free shipping\s*$/i;

  // The merchant note is static per pack value across every product on the
  // template, so "free shipping" can't live in it — append it here only when
  // this row's actual price (for the selected color/size) reaches the threshold.
  // Any "free shipping" already typed into the note is stripped first so a pack
  // under the threshold never claims it.
  function buildNoteText(note, price) {
    var base = (note || '').replace(INTC_FREE_SHIPPING_SUFFIX, '');
    if (price == null || price < INTC_FREE_SHIPPING_CENTS) return base;
    return base ? base + ' · free shipping' : 'Free shipping';
  }

  function parsePackCount(value) {
    var match = String(value).match(/\d+/);
    return parseInt((match && match[0]) || '1', 10);
  }

  function isPackFieldset(fieldset) {
    var name = (fieldset.getAttribute('data-wair-option-name') || '').toLowerCase();
    return name.indexOf('pack') !== -1;
  }

  function getPackFieldsets(root) {
    var scope = root || document;
    var all = scope.querySelectorAll('fieldset[data-wair-option-name]');
    var result = [];
    for (var i = 0; i < all.length; i++) {
      if (isPackFieldset(all[i])) result.push(all[i]);
    }
    return result;
  }

  // Resolve the section id shared by both the <variant-selects> element and
  // the intc-pack-ladder-data script tag, using the data-section attribute
  // that the existing shared markup already exposes (more reliable than
  // guessing DOM nesting depth between the picker and the data script).
  function getSectionId(fieldset) {
    var variantSelects = fieldset.closest('variant-selects');
    if (variantSelects && variantSelects.getAttribute('data-section')) {
      return variantSelects.getAttribute('data-section');
    }
    var sectionWrapper =
      fieldset.closest('[id^="shopify-section-"]') || fieldset.closest('[data-section]');
    if (sectionWrapper) {
      return (
        sectionWrapper.getAttribute('data-section') ||
        sectionWrapper.id.replace('shopify-section-', '')
      );
    }
    return null;
  }

  function getVariantsData(fieldset) {
    var sectionId = getSectionId(fieldset);
    if (!sectionId) return null;
    var script = document.getElementById('intc-pack-prices-' + sectionId);
    if (!script) return null;
    var packPosition = parseInt(script.getAttribute('data-pack-option-position'), 10);
    if (!packPosition) return null;
    var variants;
    try {
      variants = JSON.parse(script.textContent);
    } catch (err) {
      return null;
    }
    if (!Array.isArray(variants)) return null;
    return { variants: variants, packPosition: packPosition };
  }

  // Data source: snippets/intc-pack-ladder-data.liquid also emits
  //   <script type="application/json" id="intc-pack-tags-{{ section.id }}">
  //     [{ value, badge, note }, ...]
  // sourced from intc_pack_tag blocks (merchant-configured "MOST CHOSEN" /
  // "BEST VALUE" style copy) — never baked into the real variant values.
  function getPackTags(fieldset) {
    var sectionId = getSectionId(fieldset);
    if (!sectionId) return null;
    var script = document.getElementById('intc-pack-tags-' + sectionId);
    if (!script) return null;
    var tags;
    try {
      tags = JSON.parse(script.textContent);
    } catch (err) {
      return null;
    }
    if (!Array.isArray(tags)) return null;
    var map = {};
    tags.forEach(function (tag) {
      if (tag && tag.value) map[tag.value] = tag;
    });
    return map;
  }

  function getSelectedVariant(fieldset) {
    var variantSelects = fieldset.closest('variant-selects');
    if (!variantSelects) return null;
    var script = variantSelects.querySelector('script[data-selected-variant]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (err) {
      return null;
    }
  }

  function optionKey(position) {
    return 'option' + position;
  }

  function findVariantForValue(variants, packPosition, selectedVariant, value) {
    if (!variants || !selectedVariant) return null;
    var otherPositions = [1, 2, 3].filter(function (p) {
      return p !== packPosition;
    });
    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      var matchesOthers = otherPositions.every(function (p) {
        var key = optionKey(p);
        return selectedVariant[key] == null || variant[key] === selectedVariant[key];
      });
      if (matchesOthers && variant[optionKey(packPosition)] === value) {
        return variant;
      }
    }
    return null;
  }

  // The CSS restyles a per-row wrapper that holds both the <input> and its
  // <label> as children (true for the .itg_main_repeat markup emitted by
  // snippets/custom-product-variant-options.liquid). This product's own Pack
  // Size values instead go through snippets/product-variant-options.liquid,
  // which renders <input> and <label> as bare siblings with no wrapper at
  // all. Rather than maintain two different CSS layouts, reparent that pair
  // into a synthetic wrapper span the first time we see it — a plain DOM
  // move, not a clone, so all existing attributes/listeners on the input are
  // preserved and Dawn's variant-selects keeps matching it via
  // querySelectorAll('input[type="radio"]') (confirmed no direct-child-only
  // selectors in assets/global.js).
  function ensureRowWrapper(input, label) {
    var existing = input.closest('.itg_main_repeat, .intc-pack-ladder__row');
    if (existing) return existing;

    var wrapper = document.createElement('span');
    wrapper.className = 'intc-pack-ladder__row';
    input.parentNode.insertBefore(wrapper, input);
    wrapper.appendChild(input);
    if (label) wrapper.appendChild(label);

    // Make the whole card act as the click target (matches the reference
    // design's selectable-row behavior), not just the label text. Clicking
    // the input or label directly already works natively; this only forwards
    // clicks that land on the price/savings meta area appended later.
    wrapper.addEventListener('click', function (event) {
      if (event.target === input || (label && label.contains(event.target))) return;
      if (input.disabled) return;
      input.click();
    });

    return wrapper;
  }

  // Reads each option-value row inside the pack fieldset. Supports both the
  // .itg_main_repeat-wrapped markup (snippets/custom-product-variant-options.liquid)
  // and the plain input+label markup with no wrapper div
  // (snippets/product-variant-options.liquid) — see report for details.
  function getRows(fieldset) {
    var inputs = fieldset.querySelectorAll('input[type="radio"]');
    var rows = [];
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var label = null;
      if (input.id) {
        var selector =
          'label[for="' + (window.CSS && CSS.escape ? CSS.escape(input.id) : input.id) + '"]';
        label = fieldset.querySelector(selector);
      }
      var container = ensureRowWrapper(input, label);
      if (!label) label = container.querySelector('label');
      var value =
        input.getAttribute('value') || (label && label.getAttribute('data-color-title')) || '';
      rows.push({ input: input, label: label, container: container, value: value });
    }
    return rows;
  }

  function findBaselineVariant(variants, packPosition, selectedVariant, rows) {
    for (var i = 0; i < rows.length; i++) {
      if (!rows[i].value) continue;
      if (parsePackCount(rows[i].value) === 1) {
        var variant = findVariantForValue(variants, packPosition, selectedVariant, rows[i].value);
        if (variant) return variant;
      }
    }
    return null;
  }

  function formatUnitPrice(unitPriceCents, priceFormatted) {
    var unitNumber = (unitPriceCents / 100).toFixed(2);
    return priceFormatted.replace(/[\d,.]+/, unitNumber);
  }

  function renderRowMeta(row, unitPriceText, savingsPercent, totalPriceText) {
    // container is always the per-row wrapper now (ensureRowWrapper() creates
    // one when the shared markup didn't provide one), so it's safe to use
    // directly — appended as a sibling of <label> inside it.
    var host = row.container || row.label || row.input.parentElement;
    if (!host) return;

    var meta = host.querySelector('.intc-pack-ladder__meta');
    if (!meta) {
      meta = document.createElement('span');
      meta.className = 'intc-pack-ladder__meta';
      host.appendChild(meta);
    }
    // Also mark the row container so the stylesheet can restyle it into a
    // card/row layout without needing :has() support.
    host.classList.add('intc-pack-ladder__row');

    meta.innerHTML = '';

    // "$X each" and "Save X%" stack as one right-aligned column; the pack
    // total price is its own, larger column — matches the reference layout
    // (as opposed to the old single-row [unit price][badge][total] layout).
    var priceCol = document.createElement('span');
    priceCol.className = 'intc-pack-ladder__price-col';

    if (unitPriceText) {
      var unitEl = document.createElement('span');
      unitEl.className = 'intc-pack-ladder__unit-price';
      unitEl.textContent = unitPriceText + ' each';
      priceCol.appendChild(unitEl);
    }

    if (savingsPercent > 0) {
      var savingsEl = document.createElement('span');
      savingsEl.className = 'intc-pack-ladder__savings';
      savingsEl.textContent = 'Save ' + savingsPercent + '%';
      priceCol.appendChild(savingsEl);
    }

    if (priceCol.childNodes.length) meta.appendChild(priceCol);

    if (totalPriceText) {
      var totalEl = document.createElement('span');
      totalEl.className = 'intc-pack-ladder__total-price';
      totalEl.textContent = totalPriceText;
      meta.appendChild(totalEl);
    }
  }

  // Injects the merchant-configured badge ("MOST CHOSEN") next to the pack
  // name and the note line underneath it. Only fires on rows whose label
  // markup includes the .intc-pack-ladder__label-top wrapper (added in
  // snippets/intc-product-variant-options.liquid /
  // snippets/intc-custom-product-variant-options.liquid for Pack Size
  // options specifically) — silently no-ops otherwise.
  function renderRowTag(row, tag, price) {
    if (!row.label) return;
    var topWrap = row.label.querySelector('.intc-pack-ladder__label-top');
    if (!topWrap) return;

    var existingBadge = topWrap.querySelector('.intc-pack-ladder__promo-badge');
    if (existingBadge) existingBadge.remove();
    if (tag && tag.badge) {
      var badgeEl = document.createElement('span');
      badgeEl.className = 'intc-pack-ladder__promo-badge';
      badgeEl.textContent = tag.badge;
      topWrap.appendChild(badgeEl);
    }

    var contentWrap = row.label.querySelector('.intc-pack-ladder__label-content');
    var existingNote = row.label.querySelector('.intc-pack-ladder__note');
    if (existingNote) existingNote.remove();
    var noteText = tag ? buildNoteText(tag.note, price) : '';
    if (noteText && contentWrap) {
      var noteEl = document.createElement('span');
      noteEl.className = 'intc-pack-ladder__note';
      noteEl.textContent = noteText;
      contentWrap.appendChild(noteEl);
    }
  }

  function updateFieldset(fieldset) {
    var data = getVariantsData(fieldset);
    if (!data) return;

    var selectedVariant = getSelectedVariant(fieldset);
    if (!selectedVariant) return;

    var rows = getRows(fieldset);
    if (!rows.length) return;

    var tags = getPackTags(fieldset);

    var baselineVariant = findBaselineVariant(
      data.variants,
      data.packPosition,
      selectedVariant,
      rows
    );
    var baselineUnitPriceCents = null;
    if (baselineVariant) {
      var baselineCount = parsePackCount(baselineVariant[optionKey(data.packPosition)]);
      baselineUnitPriceCents = baselineVariant.price / baselineCount;
    }

    rows.forEach(function (row) {
      if (!row.value) return;

      var matchedVariant = findVariantForValue(
        data.variants,
        data.packPosition,
        selectedVariant,
        row.value
      );

      renderRowTag(row, tags && tags[row.value], matchedVariant ? matchedVariant.price : null);

      if (!matchedVariant) return; // out-of-stock / no match — leave row plain

      var count = parsePackCount(row.value);
      var unitPriceCents = matchedVariant.price / count;

      var savingsPercent = 0;
      if (baselineUnitPriceCents) {
        savingsPercent = Math.round((1 - unitPriceCents / baselineUnitPriceCents) * 100);
      }

      var unitPriceText = formatUnitPrice(unitPriceCents, matchedVariant.priceFormatted);
      renderRowMeta(row, unitPriceText, savingsPercent, matchedVariant.priceFormatted);
    });
  }

  function updateAllPackFieldsets(root) {
    getPackFieldsets(root).forEach(updateFieldset);
  }

  document.addEventListener('DOMContentLoaded', function () {
    updateAllPackFieldsets(document);
  });

  // Dawn's <variant-selects> replaces the entire picker DOM with a freshly
  // fetched server render on every option change (see
  // assets/product-info.js updateOptionValues()), which wipes out anything
  // this script injected — a raw "change" + requestAnimationFrame guess
  // fires before that fetch resolves, so the decoration gets erased right
  // after being added. PUB_SUB_EVENTS.variantChange is published by
  // product-info.js only once that replace has actually finished, so
  // subscribing to it (instead of guessing with a timer) re-decorates the
  // real final DOM every time.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function () {
      updateAllPackFieldsets(document);
    });
  }
})();
